import { useState, useRef, useEffect } from 'react';

interface Message {
  role: 'user' | 'bot';
  text: string;
  time: string;
}

const quickQuestions = [
  '什么是元宇宙？',
  '6G和5G有什么区别？',
  'Web5.0是什么？',
  '脑机接口如何工作？',
  'META66是什么平台？',
  'RWA是什么？',
];

const botAnswers: Record<string, string> = {
  '什么是元宇宙？': '元宇宙是一个持久化、去中心化的虚拟空间，融合了XR沉浸体验、数字孪生、区块链与6G超连接技术。它不是单一的虚拟世界，而是物理现实与数字现实深度融合的新型文明形态。META66认为，元宇宙是人类数字文明的下一个形态，预计2030年将有20亿日活用户。',
  '6G和5G有什么区别？': '6G相比5G有质的飞跃：峰值速率从20Gbps提升至1Tbps（提升50倍），延迟从1ms降至0.01ms（降低100倍），连接密度提升10倍。6G采用太赫兹频段，支持空天地海一体化覆盖，是元宇宙全息通信的基础设施。预计2030年全球商用。',
  'Web5.0是什么？': 'Web5.0是Web3.0去中心化与Web4.0语义智能的融合升级。它以DID（去中心化身份）为核心，实现数据主权归用户所有，AI驱动的语义理解让网络真正"懂你"。Web5.0将成为元宇宙的协议层，预计2028-2032年逐步成熟。',
  '脑机接口如何工作？': '脑机接口（BCI）通过采集大脑神经信号，将意念转化为数字指令。非侵入式BCI使用EEG头环采集脑电波，侵入式BCI（如Neuralink）直接植入芯片。目前精度已达99.7%，延迟50ms。预计2030年消费级BCI普及，实现意念控制元宇宙。',
  'META66是什么平台？': 'META66是中国领先的元宇宙垂直综合资讯与生态平台，深度聚焦元宇宙、6G超连接、Web5.0、XR沉浸体验、量子计算、脑机接口等前沿科技。平台提供深度资讯、技术解析、前沿培训、开发者生态与社区交流，致力于成为全球华人元宇宙生态的核心枢纽。',
  'RWA是什么？': 'RWA（Real World Assets，现实世界资产）是将现实中的资产（房产、债券、艺术品、大宗商品等）通过区块链技术进行代币化的过程。RWA打通了传统金融与DeFi的壁垒，让普通投资者也能参与高门槛资产投资。META66的姊妹平台 www.rwa99.cn 专注于RWA领域的深度资讯与生态建设，欢迎访问！',
};

const getTime = () => new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });

const defaultAnswer = (q: string) =>
  `感谢您的提问："${q}"。这是一个很好的问题！META66正在持续深耕元宇宙、6G、量子计算、脑机接口等前沿科技领域。建议您浏览我们的【元宇宙时讯】和【站长专栏】获取最新深度内容，或联系站长清鉴元衡（13810001256@126.com）进行深度交流。`;

function usePlanePosition() {
  const [pos, setPos] = useState({ x: 120, y: 120, angle: 0 });
  const animRef = useRef(0);

  useEffect(() => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const margin = 110;

    const animate = (timestamp: number) => {
      const t = timestamp * 0.00022;
      const denom = 1 + Math.sin(t) * Math.sin(t);
      const rawX = Math.cos(t) / denom;
      const rawY = (Math.sin(t) * Math.cos(t)) / denom;

      const cx = W / 2;
      const cy = H / 2;
      const rx = (W / 2 - margin) * 0.9;
      const ry = (H / 2 - margin) * 0.75;

      const x = cx + rawX * rx;
      const y = cy + rawY * ry;

      const dt = 0.01;
      const denom2 = 1 + Math.sin(t + dt) * Math.sin(t + dt);
      const nx = cx + (Math.cos(t + dt) / denom2) * rx;
      const ny = cy + ((Math.sin(t + dt) * Math.cos(t + dt)) / denom2) * ry;
      const angle = Math.atan2(ny - y, nx - x) * (180 / Math.PI);

      setPos({ x, y, angle });
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return pos;
}

export default function RobotAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'bot',
      text: '嗨！我是 NOVA-X，META66的星际智能向导 ✦ 我可以回答关于元宇宙、6G、Web5.0、脑机接口、RWA等前沿科技的问题。有什么想探索的吗？',
      time: getTime(),
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const planePos = usePlanePosition();

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { role: 'user', text: text.trim(), time: getTime() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      const answer = botAnswers[text.trim()] || defaultAnswer(text.trim());
      setMessages((prev) => [...prev, { role: 'bot', text: answer, time: getTime() }]);
      setTyping(false);
    }, 1200 + Math.random() * 800);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') sendMessage(input);
  };

  return (
    <>
      {/* Flying Plane - ENLARGED */}
      <div
        className="fixed z-[300] pointer-events-none"
        style={{
          left: planePos.x,
          top: planePos.y,
          transform: `translate(-50%, -50%) rotate(${planePos.angle}deg)`,
          transition: 'left 0.1s linear, top 0.1s linear',
          willChange: 'left, top, transform',
        }}
      >
        <div
          className="pointer-events-auto cursor-pointer"
          onClick={() => { setOpen(!open); setShowBubble(false); }}
          title="NOVA-X · META66星际智能向导"
          style={{ filter: 'drop-shadow(0 0 18px rgba(0,255,200,0.9)) drop-shadow(0 0 36px rgba(0,200,255,0.5))' }}
        >
          {/* ENLARGED: was 72x44, now 108x66 */}
          <svg width="108" height="66" viewBox="0 0 72 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="8" cy="22" rx="10" ry="4" fill="url(#trailGrad)" opacity="0.6"/>
            <ellipse cx="4" cy="22" rx="6" ry="2.5" fill="url(#trailGrad2)" opacity="0.4"/>
            <path d="M18 22 L58 14 L68 22 L58 30 L18 22Z" fill="url(#bodyGrad)" stroke="rgba(0,255,200,0.8)" strokeWidth="1"/>
            <ellipse cx="56" cy="22" rx="8" ry="6" fill="url(#cockpitGrad)" stroke="rgba(0,255,255,0.9)" strokeWidth="1"/>
            <ellipse cx="57" cy="21" rx="4" ry="3" fill="rgba(0,255,255,0.3)"/>
            <path d="M38 22 L50 8 L56 10 L44 22Z" fill="url(#wingGrad)" stroke="rgba(0,255,200,0.6)" strokeWidth="0.8"/>
            <path d="M38 22 L50 36 L56 34 L44 22Z" fill="url(#wingGrad)" stroke="rgba(0,255,200,0.6)" strokeWidth="0.8"/>
            <path d="M20 22 L26 16 L30 18 L24 22Z" fill="rgba(0,200,255,0.5)" stroke="rgba(0,255,200,0.4)" strokeWidth="0.6"/>
            <path d="M20 22 L26 28 L30 26 L24 22Z" fill="rgba(0,200,255,0.5)" stroke="rgba(0,255,200,0.4)" strokeWidth="0.6"/>
            <rect x="14" y="19" width="8" height="6" rx="3" fill="url(#engineGrad)" stroke="rgba(0,255,136,0.6)" strokeWidth="0.8"/>
            <ellipse cx="11" cy="22" rx="5" ry="2.5" fill="url(#flameGrad)" opacity="0.9"/>
            <ellipse cx="8" cy="22" rx="3" ry="1.5" fill="rgba(255,255,200,0.9)"/>
            <circle cx="52" cy="10" r="1.5" fill="#FF6B9D" opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="52" cy="34" r="1.5" fill="#00FF88" opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.2;0.9" dur="0.9s" repeatCount="indefinite"/>
            </circle>
            <line x1="50" y1="20" x2="62" y2="20" stroke="rgba(0,255,255,0.4)" strokeWidth="0.5"/>
            <line x1="50" y1="22" x2="64" y2="22" stroke="rgba(0,255,255,0.6)" strokeWidth="0.5"/>
            <line x1="50" y1="24" x2="62" y2="24" stroke="rgba(0,255,255,0.4)" strokeWidth="0.5"/>
            {/* Extra engine glow rings */}
            <circle cx="11" cy="22" r="6" fill="none" stroke="rgba(0,255,200,0.2)" strokeWidth="0.5">
              <animate attributeName="r" values="6;9;6" dur="1.5s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0;0.3" dur="1.5s" repeatCount="indefinite"/>
            </circle>
            <defs>
              <linearGradient id="bodyGrad" x1="18" y1="14" x2="68" y2="30" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#0A2A3A"/>
                <stop offset="50%" stopColor="#0D3D4A"/>
                <stop offset="100%" stopColor="#051520"/>
              </linearGradient>
              <linearGradient id="cockpitGrad" x1="48" y1="16" x2="64" y2="28" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#00FFCC" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#0066AA" stopOpacity="0.6"/>
              </linearGradient>
              <linearGradient id="wingGrad" x1="38" y1="8" x2="56" y2="36" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#00CCFF" stopOpacity="0.7"/>
                <stop offset="100%" stopColor="#004466" stopOpacity="0.4"/>
              </linearGradient>
              <linearGradient id="engineGrad" x1="14" y1="19" x2="22" y2="25" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#1A4A3A"/>
                <stop offset="100%" stopColor="#00FF88" stopOpacity="0.4"/>
              </linearGradient>
              <radialGradient id="flameGrad" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#FFFFFF"/>
                <stop offset="30%" stopColor="#00FFCC"/>
                <stop offset="70%" stopColor="#0088FF"/>
                <stop offset="100%" stopColor="transparent"/>
              </radialGradient>
              <radialGradient id="trailGrad" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#00FFCC" stopOpacity="0.5"/>
                <stop offset="100%" stopColor="transparent"/>
              </radialGradient>
              <radialGradient id="trailGrad2" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#0088FF" stopOpacity="0.4"/>
                <stop offset="100%" stopColor="transparent"/>
              </radialGradient>
            </defs>
          </svg>

          {/* NOVA-X label - larger */}
          <div
            className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1 text-xs font-black"
            style={{
              background: 'rgba(0,10,20,0.9)',
              border: '1.5px solid rgba(0,255,200,0.6)',
              borderRadius: '6px 4px 8px 4px / 4px 8px 4px 6px',
              color: '#00FFCC',
              fontFamily: 'Orbitron, sans-serif',
              fontSize: '11px',
              letterSpacing: '0.18em',
              backdropFilter: 'blur(4px)',
              boxShadow: '0 0 12px rgba(0,255,200,0.3)',
            }}
          >
            NOVA-X
          </div>
        </div>

        {showBubble && (
          <div
            className="absolute -top-20 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-2.5 text-sm"
            style={{
              background: 'rgba(0,10,20,0.95)',
              border: '1.5px solid rgba(0,255,200,0.5)',
              borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
              color: 'rgba(0,255,200,0.95)',
              fontFamily: 'Caveat, cursive',
              fontSize: '1rem',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 24px rgba(0,255,200,0.25)',
              animation: 'fadeInUp 0.5s ease',
            }}
          >
            点我，探索元宇宙！✦
          </div>
        )}
      </div>

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-8 right-8 z-[299] w-[400px] max-w-[calc(100vw-2rem)] flex flex-col"
          style={{
            background: 'rgba(2,8,20,0.97)',
            border: '2px solid rgba(0,255,200,0.3)',
            borderRadius: '20px 14px 20px 14px / 14px 20px 14px 20px',
            boxShadow: '0 0 60px rgba(0,255,200,0.15), 0 20px 60px rgba(0,0,0,0.9)',
            backdropFilter: 'blur(24px)',
            maxHeight: '580px',
          }}
        >
          <div
            className="flex items-center justify-between px-5 py-4 flex-shrink-0"
            style={{ borderBottom: '1px solid rgba(0,255,200,0.12)' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,255,200,0.15), rgba(0,100,200,0.2))',
                  border: '1.5px solid rgba(0,255,200,0.4)',
                  borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                }}
              >
                <svg width="26" height="16" viewBox="0 0 72 44" fill="none">
                  <path d="M18 22 L58 14 L68 22 L58 30 L18 22Z" fill="rgba(0,255,200,0.6)" stroke="rgba(0,255,200,0.9)" strokeWidth="2"/>
                  <ellipse cx="56" cy="22" rx="8" ry="6" fill="rgba(0,200,255,0.4)" stroke="rgba(0,255,255,0.8)" strokeWidth="1.5"/>
                  <path d="M38 22 L50 8 L56 10 L44 22Z" fill="rgba(0,200,255,0.5)"/>
                  <path d="M38 22 L50 36 L56 34 L44 22Z" fill="rgba(0,200,255,0.5)"/>
                </svg>
              </div>
              <div>
                <p className="text-white text-sm font-black" style={{ fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.1em' }}>NOVA-X</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00FFCC] animate-pulse" />
                  <span className="text-xs" style={{ color: 'rgba(0,255,200,0.7)', fontFamily: 'Caveat, cursive' }}>META66 星际智能向导 · 在线</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors cursor-pointer"
              style={{ borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" style={{ minHeight: 0 }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {msg.role === 'bot' && (
                  <div
                    className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      background: 'rgba(0,255,200,0.12)',
                      border: '1px solid rgba(0,255,200,0.3)',
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className="ri-flight-takeoff-line text-xs" style={{ color: '#00FFCC' }} />
                  </div>
                )}
                <div className={`max-w-[80%] flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className="px-4 py-3 text-xs leading-relaxed"
                    style={{
                      background: msg.role === 'user' ? 'rgba(0,255,200,0.12)' : 'rgba(255,255,255,0.04)',
                      border: msg.role === 'user' ? '1px solid rgba(0,255,200,0.3)' : '1px solid rgba(255,255,255,0.07)',
                      borderRadius: msg.role === 'user' ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                      color: msg.role === 'user' ? '#00FFCC' : 'rgba(255,255,255,0.75)',
                    }}
                  >
                    {msg.text}
                  </div>
                  <span className="text-white/20 text-xs">{msg.time}</span>
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex gap-2">
                <div
                  className="w-7 h-7 flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(0,255,200,0.12)',
                    border: '1px solid rgba(0,255,200,0.3)',
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className="ri-flight-takeoff-line text-xs" style={{ color: '#00FFCC' }} />
                </div>
                <div
                  className="px-4 py-3 flex items-center gap-1"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '4px 14px 14px 14px',
                  }}
                >
                  {[0, 1, 2].map((idx) => (
                    <div
                      key={idx}
                      className="w-1.5 h-1.5 rounded-full animate-bounce"
                      style={{ backgroundColor: '#00FFCC', opacity: 0.6, animationDelay: `${idx * 150}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="px-4 pb-2 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {quickQuestions.slice(0, 4).map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-xs px-3 py-1.5 transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105"
                  style={{
                    background: 'rgba(0,255,200,0.06)',
                    border: '1px solid rgba(0,255,200,0.2)',
                    borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    color: 'rgba(0,255,200,0.7)',
                    fontFamily: 'Caveat, cursive',
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div
            className="px-4 pb-4 flex-shrink-0"
            style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}
          >
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="问 NOVA-X 任何前沿科技问题..."
                className="flex-1 px-4 py-2.5 text-white text-xs placeholder-white/20 focus:outline-none transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1.5px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px 8px 14px 10px / 10px 14px 8px 12px',
                }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || typing}
                className="w-10 h-10 flex items-center justify-center transition-all duration-200 cursor-pointer disabled:opacity-40 flex-shrink-0 hover:scale-110"
                style={{
                  background: 'rgba(0,255,200,0.18)',
                  border: '1.5px solid rgba(0,255,200,0.4)',
                  borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                }}
              >
                <i className="ri-send-plane-fill text-sm" style={{ color: '#00FFCC' }} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </>
  );
}
